// <copyright file="is-image-link.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

/**
 * Whether a value is a link to an image, decided on its extension.
 *
 * Callers pass BOTH kinds of value, and the difference matters:
 *
 * - a URL, e.g. the uploader's current value. Asset URLs carry a `?tag=` content-hash cache key, and
 *   that hash is base64 - it contains no dot - so the query has to come off first or
 *   `thumbnail.png?tag=<hash>` yields an "extension" of `.png?tag=<hash>` and reports itself as
 *   not-an-image, leaving the editor showing a placeholder over a perfectly good picture.
 * - a bare filename, e.g. `isImageLink(file.name)` on the resize path in FormInputView. Here `?` and
 *   `#` are ordinary filename characters: "Drawing #2.png" is an image, and treating the `#` as a
 *   fragment delimiter would silently skip resizing it.
 *
 * So a query/fragment is only removed when the value actually looks like a URL, and the URL parser -
 * not a hand-rolled split - decides where the path ends. See filePathOf.
 *
 * Lives in its own module rather than in FormInputView so it can be unit tested: the test runner's
 * esbuild cannot transform that file's decorators to the configured target.
 */
export function isImageLink(value: string | undefined): boolean {
  if (!value) return false;

  const path = filePathOf(value);
  const extension = path.substring(path.lastIndexOf('.')).toLowerCase();

  return extension === '.jpg' || extension === '.svg' || extension === '.png' || extension === '.gif';
}

/**
 * The part of `value` whose extension we care about: a URL's decoded pathname, or the value itself
 * when it is a bare filename.
 *
 * The URL parser does the carving rather than a hand-rolled split on `?`/`#`, so the awkward cases
 * come out right for free: `%23` stays part of the filename instead of being read as a fragment,
 * `%20` and friends are decoded, and the spec decides what a `#` before a `?` means.
 */
function filePathOf(value: string): string {
  // A scheme needs `://` - `C:\pictures\Drawing #2.png` must not be mistaken for a "c:" URL. A
  // filename cannot start with `/`, so a root-relative value is unambiguously a URL; that is how
  // asset URLs arrive when apiHost is empty.
  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
  if (!hasScheme && !value.startsWith('/')) return value;

  try {
    const { pathname } = new URL(value, hasScheme ? undefined : 'http://localhost');
    try {
      return decodeURIComponent(pathname);
    } catch {
      return pathname; // malformed percent-escape; the raw pathname is still better than the query
    }
  } catch {
    return value; // not parseable after all - treat it as a plain name
  }
}
